import {
  MeteringConceptMeterFieldsExtraFieldsAufhaengung as Aufhaengung,
  MeteringConceptMeterFieldsExtraFieldsMeasurement_description as MeasurementDescription,
  MeteringConceptMeterFieldsExtraFieldsMeasuring_method as MeasuringMethod,
  MeteringConceptMeterFieldsExtraFieldsMeter_billing_relevant_or_submeter as MeterBillingRelevanceOrSubmeter,
  MeteringConceptMeterFieldsExtraFieldsTariff_type as TariffType,
  VoltageLevel
} from "../../../types/api.types";
import type { FormInputData } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import type { MesskonzeptFormData } from "./MesskonzeptForm.types";

const MEASURING_METHOD_HELP_TEXT =
  "Bis zu einer Betriebsstromstärke von 63 Ampere (Frage der Absicherung im nächsten Abfragepunkt) kann Strom direkt gemessen werden und es braucht keine Wandlermessung. Dies sollte ein Elektriker bei und mit Ihnen klären. Bei Bestandszählern erkennen Sie ggf. über einen vorliegenden Wandlerfaktor, ob eine Direktmessung oder Wandlermessung vorliegt. Bei Erzeugungsanlagen > 30 kWp ist ebenfalls eine Wandlermessung vorgeschrieben. Die Abfrage hier dient zur Ableitung vorhandener oder ggf. noch zu organisierender Wandler. Sofern neue Messwandler-Plätze geschaffen werden, ist es üblich Wandler bauseits über bspw. den Netzbetreiber oder/und Elektriker zu beschaffen und zu installieren.";
const INSTALLATION_LOCATION_HELP_TEXT =
  "Beispiele: Kellerraum 1 - Zählerschrank 1 oder Erdgeschoss, Zählerraum Gewerbe 1-5";
const METER_BILLING_RELEVANCE_HELP_TEXT =
  "Zähler, die für den Strommarkt relevante Strommengen erfassen (z. B. Strombezug aus dem oder Einspeisung in das öffentliche Netz) werden als bilanzierungsrelevant bezeichnet, da die gemessenen Strommengen u. a. an den Stromlieferanten bzw. den Direktvermarkter gemeldet (= bilanziert) werden müssen. Demgegenüber werden Zähler, die lediglich innerhalb der Kundenanlage abgerechnet werden (bspw. Teilnehmer bei Mieterstromprojekten in Vollversorgung) als „Unterzähler“ bezeichnet und sind hier entsprechend zu kennzeichnen. In einem Teilversorgungsmodell (Bilanzierung gemäß „gemeinschaftlicher Gebäudestromversorgung“ bzw. auch als PV-Only bekannt), sind alle Zähler (bis auf die PV-Erzeugungsmessung) als bilanzierungsrelevant zu kennzeichnen. Gleiches gilt für das virtuelle Summenzählermodell.";
const AUFHAENGUNG_HELP_TEXT =
  "Zähler können sich in ihrer Technik und Aufhängungsart unterscheiden. In den meisten Fällen kommt bei EVU-Stromzählern die Dreipunktaufhängung zum Einsatz. Sollte ihr Netzbetreiber oder ein anders gewählter Messstellenbetreiber Ihre Zähler betreiben, ist diese Form gängig. Ebenfalls kann hier die Anbringung eHZ (elektronische Haushaltszähler) Anwendung finden. Davon zu unterscheiden sind die sogenannten Hutschienenzähler, die aufgrund ihres Einsatzortes oft auch als Industriestromzähler bezeichnet werden. Diese Zähler sind nicht TAB-konform, genügen also nicht den ***t***echnischen ***A***nschluss***b***edingungen des Netzbetreibers. Aus diesem Grund können Hutschienenzähler nur als Unterzähler im Rahmen einer Vollversorgung zum Einsatz kommen. Sofern ein per Hutschienenzähler gemessener Mieter nicht mehr mit Mieterstrom, sondern von einem dritten Lieferanten beliefert werden möchte, muss ein TAB-konformer Zählerplatz hergestellt und ein entsprechender Zähler nachgerüstet werden.";

const MEASUREMENT_DESCRIPTION_CHOICES = [
  { label: "Unbekannt", value: MeasurementDescription.Unknown },
  { label: "Netzübergabe", value: MeasurementDescription.GridTransfer },
  {
    label: "Erzeugungsmessung",
    value: MeasurementDescription.GenerationMeasurement
  },
  { label: "Allgemeinstrom", value: MeasurementDescription.GeneralElectricity },
  { label: "Speicher", value: MeasurementDescription.Storage },
  { label: "Elektromobilität", value: MeasurementDescription.ElectricMobility },
  { label: "Wärmepumpe", value: MeasurementDescription.HeatPump },
  { label: "Mieterzähler", value: MeasurementDescription.TenantMeter }
];

const MEASURING_METHOD_CHOICES = [
  { label: "Unbekannt", value: MeasuringMethod.Unknown },
  {
    label: "Wandler bauseits vorhanden",
    value: MeasuringMethod.ExistingConverterOnSite
  },
  {
    label: "Wandler bauseits geplant",
    value: MeasuringMethod.PlannedConverterOnSite
  },
  {
    label: "Wandler geplant über MSB",
    value: MeasuringMethod.PlannedConverterViaMsb
  },
  { label: "Direktmessend", value: MeasuringMethod.DirectMeasuring }
];

const VOLTAGE_CHOICES = [
  { label: "Niederspannung", value: VoltageLevel.Low },
  {
    label: "Umspannung zur Niederspannung",
    value: VoltageLevel.MediumTransformation
  },
  { label: "Mittelspannung", value: VoltageLevel.Medium },
  {
    label: "Umspannung zur Mittelspannung",
    value: VoltageLevel.HighTransformation
  },
  { label: "Hochspannung", value: VoltageLevel.High },
  {
    label: "Umspannung zur Hochspannung",
    value: VoltageLevel.ExtraHighTransformation
  },
  { label: "Höchstspannung", value: VoltageLevel.ExtraHigh }
];

const TARIFF_TYPE_CHOICES = [
  { label: "Unbekannt", value: TariffType.Unknown },
  { label: "Standard-Lastprofil", value: TariffType.Slp },
  { label: "Registrierende Leistungsmessung", value: TariffType.Rlm }
];

const METER_BILLING_RELEVANCE_CHOICES = [
  {
    label: "Bilanzierungsrelevanter Zähler",
    value: MeterBillingRelevanceOrSubmeter.MeterBillingRelevant
  },
  { label: "Unterzähler", value: MeterBillingRelevanceOrSubmeter.Submeter }
];

const AUFHAENGUNG_CHOICES = [
  { label: "Unbekannt", value: Aufhaengung.Unknown },
  { label: "Elektronische Haushaltszähler", value: Aufhaengung.Ehz },
  { label: "Dreipunktaufhängung", value: Aufhaengung.Dpa },
  { label: "Hutschiene", value: Aufhaengung.Hutschiene }
];

export const MESSKONZEPT_FORM_FIELD_DATA = {
  measurement_description: {
    name: "measurement_description",
    label: "Bezeichnung (was wird gemessen)",
    type: "select",
    data: MEASUREMENT_DESCRIPTION_CHOICES
  },
  measuring_method: {
    name: "measuring_method",
    label: "Messart",
    type: "select",
    data: MEASURING_METHOD_CHOICES,
    helpText: MEASURING_METHOD_HELP_TEXT
  },
  voltage: {
    name: "voltage",
    label: "Liefer-/ Messspannung",
    type: "select",
    data: VOLTAGE_CHOICES
  },
  installation_location: {
    name: "installation_location",
    label: "Einbauort",
    type: "text",
    helpText: INSTALLATION_LOCATION_HELP_TEXT
  },
  installation_date: {
    name: "installation_date",
    label: "Einbaudatum",
    type: "date"
  },
  yearly_consumption: {
    name: "yearly_consumption",
    label: "Jahresverbrauch (für PV-Anlage geschätzte Erzeugung) in kWh/Jahr",
    type: "number",
    min: 0
  },
  tariff_type: {
    name: "tariff_type",
    label: "Tariftyp",
    type: "select",
    data: TARIFF_TYPE_CHOICES
  },
  meter_billing_relevant_or_submeter: {
    name: "meter_billing_relevant_or_submeter",
    label: "Bilanzierungsrelevanter Zähler oder Unterzähler",
    type: "select",
    data: METER_BILLING_RELEVANCE_CHOICES,
    helpText: METER_BILLING_RELEVANCE_HELP_TEXT
  },
  aufhaengung: {
    name: "aufhaengung",
    label: "Aufhängung",
    type: "select",
    data: AUFHAENGUNG_CHOICES,
    helpText: AUFHAENGUNG_HELP_TEXT
  },
  absicherung_in_ampere: {
    name: "absicherung_in_ampere",
    label: "Absicherung in Ampere (elektr. Stromstärke)",
    type: "number"
  }
} as const satisfies {
  [key in keyof MesskonzeptFormData]?: FormInputData<MesskonzeptFormData, key>;
};
